import { useCallback, useState } from "react";
import { useMediaQuery } from "./useMediaQuery";

export const SIDEBAR_COLLAPSED_STORAGE_KEY = "optivo-ui-sidebar-collapsed";

/**
 * Below Tailwind's `2xl`: 96rem is 1536px of viewport, since a media query
 * resolves rem against the browser's 16px rather than the app's 18px root.
 * Every laptop width folds — 1280, 1366, 1440, 1470, 1512 — which is where the
 * 288px rail cost the pricing calendar two nights; from a 1680px monitor up
 * there is room for it.
 */
export const SIDEBAR_AUTO_COLLAPSE_QUERY = "(max-width: 1535px)";

type StoredChoice = "collapsed" | "expanded";

function readStoredChoice(): StoredChoice | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
        return raw === "collapsed" || raw === "expanded" ? raw : null;
    } catch {
        return null;
    }
}

function writeStoredChoice(choice: StoredChoice): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, choice);
    } catch {
        // Non-critical: the rail still toggles, it just forgets on reload.
    }
}

/**
 * Whether the navigation rail is folded to its icon strip, and the toggle.
 *
 * Until the operator uses the toggle the rail follows the viewport, folding
 * below 1536px — it used to open at 288px on every load, whatever the screen,
 * because the choice lived in component state. Once they choose, the choice is
 * stored and outranks the viewport from then on, on every screen: a rail
 * somebody opened on a laptop stays open, because they asked for it. An
 * unreadable stored value counts as no choice rather than as either one.
 */
export function useSidebarCollapsed(): [collapsed: boolean, toggle: () => void] {
    const autoCollapsed = useMediaQuery(SIDEBAR_AUTO_COLLAPSE_QUERY);
    const [choice, setChoice] = useState<StoredChoice | null>(readStoredChoice);
    const collapsed = choice ? choice === "collapsed" : autoCollapsed;

    const toggle = useCallback(() => {
        const next: StoredChoice = collapsed ? "expanded" : "collapsed";
        setChoice(next);
        writeStoredChoice(next);
    }, [collapsed]);

    return [collapsed, toggle];
}
